import { isNotNilOrEmpty } from "client/Misc/utils";
import { useMemo } from "react";

export type EmailUrlStateOptions = {
	emailToOpen: string | undefined;
	to: string | undefined;
	cc: string | undefined;
	bcc: string | undefined;
	createNewEmail: boolean | undefined;
	draftIdToEdit: string | undefined;
	folderToOpen: string | undefined;
	mailboxToOpen: string | undefined;
};

export function useEmailUrlState(options: EmailUrlStateOptions): EmailUrlState {
	const {
		emailToOpen,
		to,
		cc,
		bcc,
		createNewEmail,
		draftIdToEdit,
		folderToOpen,
		mailboxToOpen
	} = options;

	return useMemo(
		(): EmailUrlState => {

			if (createNewEmail) {
				return {
					kind: EmailUrlStateKind.Compose,
					cc: cc ?? null,
					bcc: bcc ?? null,
					to: to ?? null
				};
			}

			if (isNotNilOrEmpty(emailToOpen)) {
				return {
					kind: EmailUrlStateKind.OpenEmail,
					emailId: emailToOpen,
					smtpAddress: mailboxToOpen ?? null
				};
			}

			if (isNotNilOrEmpty(draftIdToEdit)) {
				return {
					kind: EmailUrlStateKind.EditDraft,
					draftId: draftIdToEdit,
					smtpAddress: mailboxToOpen ?? null
				};
			}

			if (isNotNilOrEmpty(folderToOpen) || isNotNilOrEmpty(mailboxToOpen)) {
				return {
					kind: EmailUrlStateKind.OpenFolder,
					folderId: folderToOpen ?? null,
					smtpAddress: mailboxToOpen ?? null
				};
			}

			return {
				kind: EmailUrlStateKind.Default
			};
		},
		[
			bcc,
			cc,
			createNewEmail,
			draftIdToEdit,
			emailToOpen,
			folderToOpen,
			mailboxToOpen,
			to
		]
	);
}

export enum EmailUrlStateKind {
	Default = 0,
	Compose = 1,
	OpenEmail = 2,
	EditDraft = 3,
	OpenFolder = 4
}

type ComposeEmailUrlState = {
	kind: EmailUrlStateKind.Compose;

	to: string | null;
	cc: string | null;
	bcc: string | null;
};

type OpenEmailUrlState = {
	kind: EmailUrlStateKind.OpenEmail;
	emailId: string;
	smtpAddress: string | null;
};

type EditDraftUrlState = {
	kind: EmailUrlStateKind.EditDraft;
	draftId: string;
	smtpAddress: string | null;
};

type OpenFolderUrlState = {
	kind: EmailUrlStateKind.OpenFolder;
	folderId: string | null;
	smtpAddress: string | null;
};

type DefaultEmailUrlState = {
	kind: EmailUrlStateKind.Default;
};

export type EmailUrlState =
	| ComposeEmailUrlState
	| OpenEmailUrlState
	| EditDraftUrlState
	| DefaultEmailUrlState
	| OpenFolderUrlState;